import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { containerVariants, itemVariants } from '../../utils/animations';
import creditApi from '../../api/creditApi';
import debtorApi from '../../api/debtorApi';
import CreditHeader from '../../components/credit/CreditHeader';
import CreditStats from '../../components/credit/CreditStats';
import CreditStatusChart from '../../components/credit/CreditStatusChart';
import CreditQuickActions from '../../components/credit/CreditQuickActions';
import CreditRecentList from '../../components/credit/CreditRecentList';

export default function CreditDashboard() {
  const [stats, setStats] = useState({
    totalApplications: 0,
    drafts: 0,
    inAnalysis: 0,
    pendingApproval: 0,
    approved: 0,
    rejected: 0,
    totalAmount: 0,
  });
  const [recentApplications, setRecentApplications] = useState<any[]>([]);
  const [statusData, setStatusData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [applications, debtors] = await Promise.all([creditApi.getAll(), debtorApi.getAll()]);

      const drafts = applications.filter(a => a.status === 'draft');
      const inAnalysis = applications.filter(a => a.status === 'analysis');
      const pending = applications.filter(a => a.status === 'approval');
      const approved = applications.filter(a => a.status === 'approved');
      const rejected = applications.filter(a => a.status === 'rejected');
      const totalAmount = approved.reduce((sum, app) => sum + (app.approved_amount || 0), 0);

      // Status distribution for pie chart
      const statusDistribution = [
        { name: 'Draft', value: drafts.length, color: '#94a3b8' },
        { name: 'Analisis', value: inAnalysis.length, color: '#3b82f6' },
        { name: 'Approval', value: pending.length, color: '#f59e0b' },
        { name: 'Disetujui', value: approved.length, color: '#10b981' },
        { name: 'Ditolak', value: rejected.length, color: '#ef4444' },
      ].filter(s => s.value > 0);

      // Add debtor info to recent applications
      const recentWithDebtor = applications.slice(0, 5).map(app => {
        const debtor = debtors.find(d => d.id === app.debtor_id);
        return { ...app, debtor_name: debtor?.full_name || '-' };
      });

      setStats({
        totalApplications: applications.length,
        drafts: drafts.length,
        inAnalysis: inAnalysis.length,
        pendingApproval: pending.length,
        approved: approved.length,
        rejected: rejected.length,
        totalAmount,
      });

      setStatusData(statusDistribution);
      setRecentApplications(recentWithDebtor);
    } catch (error) {
      // Error loading dashboard
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <CreditHeader />
      </motion.div>

      <motion.div variants={itemVariants}>
        <CreditStats stats={stats} />
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6">
        <motion.div variants={itemVariants}>
          <CreditStatusChart statusData={statusData} />
        </motion.div>
        <motion.div variants={itemVariants}>
          <CreditQuickActions />
        </motion.div>
      </div>

      <motion.div variants={itemVariants}>
        <CreditRecentList applications={recentApplications} />
      </motion.div>
    </motion.div>
  );
}
